import { Close, Refresh } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type React from "react";
import styles from "../styles.module.scss";

type ThreadItem = {
  id?: string;
  threadId?: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
};

interface ChatHistoryPanelProps {
  open: boolean;
  title?: string;
  loading?: boolean;
  threads: ThreadItem[];
  onClose: () => void;
  onRefresh?: () => void;
  onLoadThread: (thread: ThreadItem) => void;
  // 表示位置: overlay（既存モーダル風） or sidebar（左サイドバー）
  placement?: "overlay" | "sidebar";
  // 現在選択中のスレッドID
  activeThreadId?: string;
}

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  open,
  title = "履歴",
  loading = false,
  threads,
  onClose,
  onRefresh,
  onLoadThread,
  placement = "overlay",
  activeThreadId,
}) => {
  if (!open) return null;

  return (
    <div
      className={placement === "sidebar" ? styles["historySidebar"] : styles["historyPanel"]}
      role="region"
      aria-label="チャット履歴"
    >
      <div className={styles["historyHeader"]}>
        <h3 className={styles["historyTitle"]}>{title}</h3>
        <div className={styles["historyActions"]}>
          {onRefresh && (
            <Tooltip title="再読み込み" placement="top">
              <button
                type="button"
                className={styles["iconButton"]}
                onClick={onRefresh}
                aria-label="履歴を再読み込み"
              >
                <Refresh />
              </button>
            </Tooltip>
          )}
          <Tooltip title="閉じる" placement="top">
            <button
              type="button"
              className={styles["iconButton"]}
              onClick={onClose}
              aria-label="履歴を閉じる"
            >
              <Close />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className={styles["historyBody"]}>
        {loading ? (
          <div className={styles["loadingRow"]}>読み込み中...</div>
        ) : threads.length === 0 ? (
          <div className={styles["emptyRow"]}>履歴がありません</div>
        ) : (
          <ul className={styles["historyList"]}>
            {threads.map((t) => {
              const threadId = t.id || t.threadId;
              const isActive = activeThreadId && threadId === activeThreadId;

              return (
                <li
                  key={threadId}
                  className={isActive ? styles["historyItemActive"] : styles["historyItem"]}
                  onClick={() => onLoadThread(t)}
                  aria-label={isActive ? "現在選択中のスレッド" : "このスレッドを読み込む"}
                >
                  <div className={styles["threadMain"]}>
                    <div className={styles["threadTitle"]} title={t.title || "新しいチャット"}>
                      {t.title || "新しいチャット"}
                    </div>
                    <div className={styles["threadMetaTime"]}>
                      {new Date(t.updatedAt || t.createdAt || Date.now()).toLocaleString("ja-JP", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChatHistoryPanel;
